import apiClient from '../utils/apiClient';

const base = (facilityId) => `/api/facilities/${facilityId}/dashboard`;

const dashboardService = {
  getStats: (facilityId) => apiClient.get(`${base(facilityId)}/stats`),
  getRecentActivity: (facilityId) => apiClient.get(`${base(facilityId)}/recent-activity`),
  getOverview: (facilityId) => apiClient.get(`${base(facilityId)}/overview`),
  getOccupancySnapshot: (facilityId) => apiClient.get(`${base(facilityId)}/occupancy-snapshot`),
  getRevenuePulse: (facilityId) => apiClient.get(`${base(facilityId)}/revenue-pulse`),
  getDelinquencyQueue: (facilityId, limit = 10) =>
    apiClient.get(`${base(facilityId)}/delinquency-queue`, { params: { limit } }),
  getActivityFeed: (facilityId, limit = 20) =>
    apiClient.get(`${base(facilityId)}/activity-feed`, { params: { limit } }),
  getTodayTasks: (facilityId) => apiClient.get(`${base(facilityId)}/today-tasks`),
  // ESS-style operational KPI tiles — move-ins/outs this month, waiting
  // list, and the website / recurring-billing mix. Also bundled into
  // /overview as `operations`, so the dashboard rarely calls this directly.
  getOperationsMetrics: (facilityId) =>
    apiClient.get(`${base(facilityId)}/operations-metrics`),
  // Charts: monthly collected-revenue series (last `months` calendar
  // months) + MoM/YoY deltas. Same payment source as revenue-pulse, so the
  // current month reconciles with the MTD KPI.
  getRevenueHistory: (facilityId, months = 12) =>
    apiClient.get(`${base(facilityId)}/revenue-history`, { params: { months } }),
  // Charts: outstanding balance bucketed by days-past-due for the aging
  // donut. Buckets partition the same rows behind the Past-due KPI, so
  // total_outstanding === the Past-due headline.
  getAgingBuckets: (facilityId) =>
    apiClient.get(`${base(facilityId)}/aging-buckets`),
  // Charts: reconstructed monthly occupancy % for the last `months`
  // month-ends, derived from real rental start/end dates (active-at-date /
  // current total units). Series is [{month, occupancy_pct, occupied, total}].
  getOccupancyTrend: (facilityId, months = 12) =>
    apiClient.get(`${base(facilityId)}/occupancy-trend`, { params: { months } }),
  // Drill-down: constituent rows behind a KPI. `metric` ∈ revenue |
  // occupancy | past_due | leads | monthly_revenue | active_rentals.
  // `params` carries metric extras — revenue accepts `month` (YYYY-MM,
  // facility calendar) so a revenue-history bar click drills ITS month
  // instead of always the current one.
  getBreakdown: (facilityId, metric, params = {}) =>
    apiClient.get(`${base(facilityId)}/breakdown`,
      { params: { metric, ...params } }),
  // Unified "prospect needing attention" pool — open leads + no-rental
  // waitlist customers + abandoned rent-wizard drafts, de-duplicated.
  // Returns { count, items: [{ id, name, source, when, link }] }. Backs the
  // flashing "new prospect" banner; polled while the operator sits on the
  // dashboard so a fresh signup/lead surfaces without a reload.
  getPendingProspects: (facilityId, limit = 8) =>
    apiClient.get(`${base(facilityId)}/pending-prospects`, { params: { limit } }),
};

export default dashboardService;
